import { type PostHog } from '@posthog/react';
import { getPurchaseTimeBand } from '../../utils/posthog/getPurchaseTimeBand';
import { getPricingBand } from '../../utils/posthog/getPricingBand';
import { log } from '@domgen/shared/utils/log';
import type { PostHogEvent } from '../../enums/postHogEvents';
import { FormKey } from '../../enums/formKey';
import { StepKey } from '../../enums/stepKey';
import { getFormRegionOrStateCode } from '../../utils/posthog/getRegionOrStateCode';
import { getFormApplianceName } from '../../utils/posthog/getFormApplianceName';
import { StoreSnapshot } from '@xstate/store-react';
import { parseLocalDateString } from '@domgen/shared/src/utils/date/parseLocalDateString';
import { StoreContext } from '../../models/store/context';

/**
 * Utility function to capture a PostHog event with the given name and properties.
 * @param {PostHog | null} posthog - The PostHog instance obtained from the usePostHog hook, if not available, pass null, will log warning.
 * @param {PostHogEvent} eventName - The name of the event to capture.
 * @param {StoreContext} storeContext - The current state of the store, used to extract properties for the event. If form data is not available, will log debug and send event with empty properties.
 */
export const capturePostHogEvent = (
  posthog: PostHog | null,
  eventName: PostHogEvent | string,
  storeContext: StoreSnapshot<StoreContext>,
): void => {
  if (!posthog) {
    log.warn({
      message: `post hog event ${eventName} was fired but posthog was missing so event not send to analytics`,
    });
  } else {
    let data: Record<string, unknown> = {};
    if (!storeContext.context.form) {
      log.debug({ message: `${eventName} was fired with not properties attached` });
    } else {
      const formData = storeContext.context.form;
      data = {
        applianceName: getFormApplianceName(formData, storeContext.context.applianceLookupResults),
        regionOrStateCode: getFormRegionOrStateCode(formData),
        purchaseDate: formData[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE]
          ? getPurchaseTimeBand(
              parseLocalDateString(formData[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE]),
            )
          : undefined,
        purchasePrice: formData[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE]
          ? getPricingBand(Number(formData[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE]))
          : undefined,
      };
    }
    log.debug({
      message: `${eventName} fired, see data`,
      data,
    });
    posthog.capture(eventName, data);
  }
};
